import type { CampaignRecipient, MarketingCampaign } from '@otw/models/leads/marketingCampaign'
import { db } from '@otw/models/db'
import { useQuery } from '@tanstack/react-query'
import { doc, getDoc, getDocs, query, where } from 'firebase/firestore'

export const campaignRecipientsQueryKey = ['campaign_recipients'] as const

export interface ContactMarketingHistoryRow {
  recipient: CampaignRecipient
  campaignName: string
}

export function useCampaignRecipients(campaignId: string | undefined) {
  return useQuery({
    queryKey: [...campaignRecipientsQueryKey, 'campaign', campaignId],
    enabled: Boolean(campaignId),
    queryFn: async (): Promise<CampaignRecipient[]> => {
      if (!campaignId) return []
      const snap = await getDocs(
        query(db.campaign_recipients, where('campaignId', '==', campaignId)),
      )
      return snap.docs
        .map((item) => item.data())
        .sort((a, b) => {
          const aTime = a.sentAt?.getTime() ?? a.createdAt.getTime()
          const bTime = b.sentAt?.getTime() ?? b.createdAt.getTime()
          return bTime - aTime
        })
    },
  })
}

export function useContactMarketingHistory(contactId: string | undefined) {
  return useQuery({
    queryKey: [...campaignRecipientsQueryKey, 'contact', contactId],
    enabled: Boolean(contactId),
    queryFn: async (): Promise<ContactMarketingHistoryRow[]> => {
      if (!contactId) return []
      const snap = await getDocs(
        query(db.campaign_recipients, where('contactId', '==', contactId)),
      )
      const recipients = snap.docs.map((item) => item.data())
      const campaignIds = [...new Set(recipients.map((row) => row.campaignId).filter(Boolean))]
      const campaignSnaps = await Promise.all(
        campaignIds.map((id) => getDoc(doc(db.marketing_campaigns, id))),
      )
      const nameById = new Map<string, string>()
      for (const campaignSnap of campaignSnaps) {
        const campaign = campaignSnap.data() as MarketingCampaign | undefined
        nameById.set(
          campaignSnap.id,
          campaign?.name?.trim() || 'Untitled campaign',
        )
      }
      return recipients
        .map((recipient) => ({
          recipient,
          campaignName: nameById.get(recipient.campaignId) || 'Untitled campaign',
        }))
        .sort((a, b) => {
          const aTime =
            a.recipient.sentAt?.getTime() ?? a.recipient.createdAt.getTime()
          const bTime =
            b.recipient.sentAt?.getTime() ?? b.recipient.createdAt.getTime()
          return bTime - aTime
        })
    },
  })
}
